/* eslint-disable consistent-return */
import { check } from 'meteor/check';
import { cloneDeep } from 'lodash';
import { HTTP } from 'meteor/http';
import { Meteor } from 'meteor/meteor';
import * as mapOps from '../map-operations';
import Maps from '../Maps';
import { FeaturePoint } from '../../SchemaUtilities/GeoJSONSchema';
import { isHTTPResultOK } from '../../../modules/fetch-utilites';

import checkAuthentication from '../../../modules/server/check-authentication';
import handleMethodException from '../../../modules/handle-method-exception';
import methodPromise from '../../../modules/method-promise-wrapper';

const editMapSchema = Maps.schema.omit(
  'createdAt',
  'deleted',
  'owner',
  'publicId',
  'publicPrivateId',
  'shared',
  'sharedPrivate',
  'updatedAt',
  'withLocation',
  'withLocationPrivate'
);
editMapSchema.extend({ _id: String });

/**
 * @typedef {object} MapQuery
 * @property {number} totalCount The total number of maps not in the trash.
 * @property {number} deletedCount The total number of maps in the trash.
 * @property {object[]} maps The maps not in the trash in the current page.
 * @property {object[]} deletedMaps The maps in the trash.
 */

/**
 * Copies the map in the database.
 *
 * @param {string} mapId - The map id.
 * @param {string} userId - The user id that wants the map.
 * @returns {string} Return the id of the new map copied.
 * @throws {Error} Meteor Error if anything goes wrong.
 */

export const copyMap = (mapId, userId) => {
  check(mapId, String);
  check(userId, String);
  try {
    checkAuthentication();
    const copyMapData = Maps.findOne({ _id: mapId });
    delete copyMapData.owner;
    delete copyMapData._id;
    delete copyMapData.createdAt;
    delete copyMapData.updatedAt;
    delete copyMapData.shared;
    delete copyMapData.withLocation;
    delete copyMapData.withLocationPrivate;
    delete copyMapData.sharedPrivate;
    delete copyMapData.publicPrivateId;
    delete copyMapData.publicId;
    delete copyMapData.deleted;
    copyMapData.name += ' - copy';
    const copyMapId = Maps.insert({ owner: userId, ...copyMapData });
    return copyMapId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes the map from database.
 *
 * @param {string} mapId - The map id.
 * @returns {string} Return the id of the deleted map.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const hardDeleteMap = mapId => {
  check(mapId, String);
  try {
    checkAuthentication();
    return Maps.remove(mapId);
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Inserts the map in the database.
 *
 * @async
 * @param {object} mapObject - The map object.
 * @param {string} userId - The user id.
 * @returns {string} Return the id of the new map.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insertMap = async (mapObject, userId) => {
  check(userId, String);
  try {
    checkAuthentication();
    let mapToSave = mapObject;
    if (mapObject.mapType === 'esri') {
      const webMapdId = await methodPromise(
        'esri.addWebMapItem',
        mapObject.name,
        mapObject.description
      );
      const clonedMap = cloneDeep(mapObject);
      clonedMap.esriWebMapId = webMapdId;
      mapToSave = clonedMap;
    }
    Maps.schema.validate(mapToSave, {
      check,
      keys: [
        'name',
        'mapType',
        'description',
        'layersOverlayed',
        'selectedButtons',
        'esriWebMapId',
        'layers',
        'imageUrl',
        'isFeature',
        'pageUrl',
        'logoUrl',
        'mapLocation',
        'baseMaps',
        'backgroundColor',
        'layoutJson',
        'templates'
      ]
    });
    return Maps.insert({ owner: userId, ...mapToSave });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Restores the map in the database.
 *
 * @param {string} mapId - The map id.
 * @returns {string} Return the id of the map.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const restoreMap = mapId => {
  check(mapId, String);
  try {
    checkAuthentication();
    return Maps.update(mapId, { $set: { deleted: 'no' } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Saves the new order of the map base maps in the database.
 *
 * @param {string} mapId - The map id.
 * @param {string[]} baseMapArray - The array contaning the Ids of the base mapslayers.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const saveNewBaseMapOrderMap = (mapId, baseMapArray) => {
  check(mapId, String);
  check(baseMapArray, Array);
  try {
    checkAuthentication();
    Maps.update(mapId, { $set: { baseMaps: baseMapArray } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Saves the new order of the map layers in the database.
 *
 * @param {string} mapId - The map id.
 * @param {string[]} layersArray - The array contaning the Ids of the map layers.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const saveNewLayerOrderMap = (mapId, layersArray) => {
  check(mapId, String);
  check(layersArray, Array);
  try {
    checkAuthentication();
    Maps.update(mapId, { $set: { layers: layersArray } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Send an unique location or a group of locations.
 *
 * @param {string[]} userLocation - The location to send.
 * @returns {string} Return the id of the map.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const sendUserLocation = async ({ userLocation }) => {
  let USER_LOCATION_API;
  try {
    checkAuthentication();
    try {
      const APISettings = await methodPromise('admin.fetchAPISetting');
      if (APISettings.length > 0) {
        USER_LOCATION_API = APISettings[0].sendUserLocationAPI;
      }
    } catch (error) {
      handleMethodException(error);
    }
    const response = HTTP.post(USER_LOCATION_API, {
      headers: { 'Content-Type': 'application/json' },
      data: { user: Meteor.userId(), object: userLocation }
    });
    return isHTTPResultOK(response);
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Soft deletes the map in the database.
 *
 * @param {string} mapId - The map id.
 * @returns {string} Return the id of the map.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const softDeleteMap = mapId => {
  check(mapId, String);
  try {
    checkAuthentication();
    return Maps.update(mapId, { $set: { deleted: new Date().toISOString() } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Toggles the map location.
 *
 * @param {string} mapId - The map id.
 * @param {boolean} isWithLocation - The new value for the location key.
 * @throws {Error} Meteor Error if anything goes wrong.
 */

export const toggleWithLocation = (mapId, isWithLocation) => {
  check(mapId, String);
  check(isWithLocation, Boolean);
  try {
    checkAuthentication();
    Maps.update(mapId, { $set: { withLocation: isWithLocation } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Toggles the map shared or unshared.
 *
 * @param {string} mapId - The map id.
 * @param {boolean} isShared - The new value for the shared key.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const toggleSharedMap = (mapId, isShared) => {
  check(mapId, String);
  check(isShared, Boolean);
  try {
    checkAuthentication();
    Maps.update(mapId, { $set: { shared: isShared } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Toggles the map shared or unshared.
 *
 * @param {string} mapId - The map id.
 * @param {boolean} isShared - The new value for the shared key.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const toggleSharedPrivateMap = (mapId, isShared) => {
  check(mapId, String);
  check(isShared, Boolean);
  try {
    checkAuthentication();
    Maps.update(mapId, { $set: { sharedPrivate: isShared } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Toggles the map location private.
 *
 * @param {string} mapId - The map id.
 * @param {boolean} isWithLocationPrivate - The new value for the location key.
 * @throws {Error} Meteor Error if anything goes wrong.
 */

export const toggleWithLocationPrivate = (mapId, isWithLocationPrivate) => {
  check(mapId, String);
  check(isWithLocationPrivate, Boolean);
  try {
    checkAuthentication();
    Maps.update(mapId, { $set: { withLocationPrivate: isWithLocationPrivate } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates the map in the database.
 *
 * @param {object} mapObject - The map object.
 * @returns {string} Return the id of the updated map.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const updateMap = mapObject => {
  try {
    checkAuthentication();
    editMapSchema.validate(mapObject);
    const mapId = mapOps.getId(mapObject);
    Maps.update(mapId, { $set: mapObject });
    return mapId;
    // Return _id so we can redirect to map after update.
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates Map background color on user request.
 *
 * @param {string} mapId - The map id.
 * @param {FeaturePoint} newBackgroundColor - The new map background color.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const updateMapBackgroundColor = (mapId, newBackgroundColor) => {
  check(mapId, String);
  check(newBackgroundColor, String);
  try {
    checkAuthentication();
    Maps.update(mapId, { $set: { backgroundColor: newBackgroundColor } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates Map map location on user request.
 *
 * @param {string} mapId - The map id.
 * @param {FeaturePoint} newMapLocation - The new map location.
 * @returns {string} Return the id of the updated map.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const updateMapLocationMap = (mapId, newMapLocation) => {
  check(mapId, String);
  try {
    checkAuthentication();
    FeaturePoint.validate(newMapLocation);
    return Maps.update(mapId, { $set: { mapLocation: newMapLocation } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates an unique variable of the map in the database.
 *
 * @param {string} mapId - The layer id.
 * @param {object} newdata - The new values of the map configuration.
 * @returns {string} Return the id of the updated map.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const updateMapTemplate = (mapId, newdata) => {
  check(mapId, String);
  try {
    checkAuthentication();
    // FeaturePoint.validate(newdata);
    return Maps.update(mapId, { $set: { templates: newdata } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates an unique variable of the map in the database.
 *
 * @param {string} mapId - The layer id.
 * @param {object} newdata - The new values of the map configuration.
 * @returns {string} Return the id of the updated map.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const updateMapVar = (mapId, newdata) => {
  check(mapId, String);
  try {
    checkAuthentication();
    // FeaturePoint.validate(newdata);
    return Maps.update(mapId, { $set: { layoutJson: newdata } });
  } catch (exception) {
    handleMethodException(exception);
  }
};
